import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { clientMutation, clientQuery } from "Core/data/GraphQLApi";
import { endpoints } from "Core/defaultValues";
import { ExceptionManager } from "Core/logManager";

//STATE INITIAL
const INIT_STATE = {
  livekitUrl: null,
  room: null,
  token: null,
  deviceConnected: null,
  deviceStatus: null,
  gpsStatus: null,
  heartbeatStatus: null,
  error: null,
  loading: false,
  stopLoading: false,
  statusLoading: false,
};

const route = "modules/.common/components/MitacVideo/redux/slice";

// Start Mitac Live Streaming
export const startMitacLiveStreaming = createAsyncThunk(
  "mitac/startStreaming",
  async ({ deviceId }, { rejectWithValue }) => {
    try {
      const data = await clientMutation(
        `mutation startMitacLiveStreaming ($deviceId: String!) {
          startMitacLiveStreaming (deviceId: $deviceId) {
            livekit_url
            room
            token
            device_id
            device_connected
            device_status
          }
        }`,
        {
          deviceId,
        },
        endpoints.GRAPHQL_GENERAL_V2
      );

      return data.startMitacLiveStreaming;
    } catch (exc) {
      ExceptionManager(exc, route, "mitac/startStreaming");
      return rejectWithValue(exc);
    }
  }
);

// Join Mitac Live Streaming — start() only wakes the device (token always null);
// join() is the call that actually returns the LiveKit token once the device is ready.
export const joinMitacLiveStreaming = createAsyncThunk(
  "mitac/joinStreaming",
  async ({ deviceId }, { rejectWithValue }) => {
    try {
      const data = await clientMutation(
        `mutation joinMitacLiveStreaming ($deviceId: String!) {
          joinMitacLiveStreaming (deviceId: $deviceId) {
            device_id
            device_connected
            device_status
            livekit_url
            room
            token
          }
        }`,
        {
          deviceId,
        },
        endpoints.GRAPHQL_GENERAL_V2
      );

      return data.joinMitacLiveStreaming;
    } catch (exc) {
      ExceptionManager(exc, route, "mitac/joinStreaming");
      return rejectWithValue(exc);
    }
  }
);

// Stop Mitac Live Streaming
export const stopMitacLiveStreaming = createAsyncThunk(
  "mitac/stopStreaming",
  async ({ deviceId }, { rejectWithValue }) => {
    try {
      const data = await clientMutation(
        `mutation stopMitacLiveStreaming ($deviceId: String!) {
          stopMitacLiveStreaming (deviceId: $deviceId) {
            device_id
            device_status
          }
        }`,
        {
          deviceId,
        },
        endpoints.GRAPHQL_GENERAL_V2
      );

      return data.stopMitacLiveStreaming;
    } catch (exc) {
      ExceptionManager(exc, route, "mitac/stopStreaming");
      return rejectWithValue(exc);
    }
  }
);

// Get Mitac Live Streaming Status
export const getMitacLiveStreamingStatus = createAsyncThunk(
  "mitac/getStatus",
  async ({ deviceId }, { rejectWithValue }) => {
    try {
      const data = await clientQuery(
        `query getMitacLiveStreamingStatus ($deviceId: String!) {
          getMitacLiveStreamingStatus (deviceId: $deviceId) {
            device_id
            device_status
            gps_status
            heartbeat_status
          }
        }`,
        {
          deviceId,
        },
        endpoints.GRAPHQL_GENERAL_V2
      );

      return data.getMitacLiveStreamingStatus;
    } catch (exc) {
      ExceptionManager(exc, route, "mitac/getStatus");
      return rejectWithValue(exc);
    }
  }
);

//Slice
const mitacRedux = createSlice({
  name: "mitac",
  initialState: INIT_STATE,
  reducers: {
    resetMitac(state, action) {
      return action.payload ? { ...state, ...action.payload } : { ...INIT_STATE };
    },
  },
  extraReducers: (builder) => {
    // startMitacLiveStreaming
    builder.addCase(startMitacLiveStreaming.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.livekitUrl = null;
      state.room = null;
      state.token = null;
      state.deviceConnected = null;
    });
    builder.addCase(startMitacLiveStreaming.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      state.livekitUrl = action.payload?.livekit_url;
      state.room = action.payload?.room;
      state.token = action.payload?.token;
      state.deviceConnected = action.payload?.device_connected;
      state.deviceStatus = action.payload?.device_status;
    });
    builder.addCase(startMitacLiveStreaming.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
      state.livekitUrl = null;
      state.room = null;
      state.token = null;
    });

    // joinMitacLiveStreaming
    builder.addCase(joinMitacLiveStreaming.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.livekitUrl = null;
      state.room = null;
      state.token = null;
      state.deviceConnected = null;
    });
    builder.addCase(joinMitacLiveStreaming.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      state.livekitUrl = action.payload?.livekit_url;
      state.room = action.payload?.room;
      state.token = action.payload?.token;
      state.deviceConnected = action.payload?.device_connected;
      state.deviceStatus = action.payload?.device_status;
    });
    builder.addCase(joinMitacLiveStreaming.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
      state.livekitUrl = null;
      state.room = null;
      state.token = null;
    });

    // stopMitacLiveStreaming
    builder.addCase(stopMitacLiveStreaming.pending, (state) => {
      state.stopLoading = true;
    });
    builder.addCase(stopMitacLiveStreaming.fulfilled, (state, action) => {
      state.stopLoading = false;
      state.deviceStatus = action.payload?.device_status ?? state.deviceStatus;
    });
    builder.addCase(stopMitacLiveStreaming.rejected, (state, action) => {
      state.stopLoading = false;
      state.error = action.payload;
    });

    // getMitacLiveStreamingStatus
    builder.addCase(getMitacLiveStreamingStatus.pending, (state) => {
      state.statusLoading = true;
    });
    builder.addCase(getMitacLiveStreamingStatus.fulfilled, (state, action) => {
      state.statusLoading = false;
      state.deviceStatus = action.payload?.device_status;
      state.gpsStatus = action.payload?.gps_status;
      state.heartbeatStatus = action.payload?.heartbeat_status;
    });
    builder.addCase(getMitacLiveStreamingStatus.rejected, (state, action) => {
      state.statusLoading = false;
      state.error = action.payload;
    });
  },
});

// Extract and export the action creators object and the reducer
export const { actions, reducer } = mitacRedux;

// Extract and export each action creator by name
export const { resetMitac } = actions;

// Export the reducer, either as a default or named export
export default reducer;
